import React, { useEffect, useState } from 'react'

import { Button, Typography } from '@mui/material'
import { Box, useMediaQuery, useTheme } from '@mui/system'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { brainstormText } from 'content/brainstormText'

import { TeeterTotterPrompt } from 'models/ideaModels'

import { useToastContext } from 'contexts/ToastProvider'

import ButtonWithIconComponent from 'components/ButtonWithIcon'
import GeneratedExampleDropdown from 'components/GeneratedExampleDropdown'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import editIcon from 'assets/images/editIcon.svg'
import refreshIcon from 'assets/images/refreshIcon.svg'

import { commonStyles } from 'styles/common.styles'

import Node from '../Node'
import { styles } from './Section2.styles'

export const TEST_ID = 'teeter-totter-section2'

const subject = {
	left: 'Extreme 1',
	center: 'Topic',
	right: 'Extreme 2',
}

export type Props = {
	centerCardValue: string
	changePrompts: (prompts: TeeterTotterPrompt) => void
	currentResponses: string
	isAiLoading: boolean
	isLoading: boolean
	leftCardValue: string
	onEditPromptClick?: (editCardValue: {
		center: string
		left: string
		right: string
	}) => void
	onRefreshPromptClick: () => void
	rightCardValue: string
	teeterTotterPrompt: TeeterTotterPrompt
}

const mobileCards = (
	isLoading: boolean,
	isEdit: boolean,
	leftCardValue: string,
	centerCardValue: string,
	rightCardValue: string,
	onInputChange: (oldValue: string, newValue: string) => void
) => {
	return (
		<Box
			sx={styles.cards}
			flexDirection={'column'}
			data-testid={`${TEST_ID}__mobile-cards`}
			alignItems={'center'}
		>
			<Box display="flex-column" width="360px" alignItems={'center'}>
				<Node
					isLoading={isLoading}
					positions={'center'}
					text={centerCardValue}
					isEdit={isEdit}
					onInputChange={onInputChange}
					subjectText={subject.center}
				/>
				<Node
					isLoading={isLoading}
					positions={'left'}
					text={leftCardValue}
					isEdit={isEdit}
					onInputChange={onInputChange}
					subjectText={subject.left}
				/>
				<Node
					isLoading={isLoading}
					positions={'right'}
					text={rightCardValue}
					isEdit={isEdit}
					onInputChange={onInputChange}
					subjectText={subject.right}
				/>
			</Box>
		</Box>
	)
}

const desktopAndTabletCards = (
	isLoading: boolean,
	isEdit: boolean,
	leftCardValue: string,
	centerCardValue: string,
	rightCardValue: string,
	onInputChange: (oldValue: string, newValue: string) => void
) => {
	return (
		<Box display={'flex'} data-testid={`${TEST_ID}__desktop-cards`}>
			<Node
				isLoading={isLoading}
				text={leftCardValue}
				positions={'left'}
				isEdit={isEdit}
				onInputChange={onInputChange}
				subjectText={subject.left}
			/>
			{/* Left arrow */}
			<Box display={'flex'} sx={styles.container}>
				<svg
					height="16"
					viewBox="0 0 270 16"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M0.792892 7.29291C0.402374 7.68343 0.402374 8.3166 0.792893 8.70712L7.15686 15.0711C7.54738 15.4616 8.18054 15.4616 8.57108 15.0711C8.96159 14.6806 8.96159 14.0474 8.57108 13.6569L2.91422 8.00001L8.57107 2.34316C8.96159 1.95264 8.96159 1.31947 8.57107 0.928946C8.18054 0.538422 7.54738 0.538422 7.15686 0.928947L0.792892 7.29291ZM268.5 7L1.5 7.00001L1.5 9.00001L268.5 9L268.5 7Z"
						fill="url(#paint0_linear_8266_87768)"
					/>
					<defs>
						<linearGradient
							id="paint0_linear_8266_87768"
							x1="268.5"
							y1="8.00001"
							x2="2.49997"
							y2="6.00252"
							gradientUnits="userSpaceOnUse"
						>
							<stop stopColor="#FF437B" />
							<stop offset="1" stopColor="#C8003C" />
						</linearGradient>
					</defs>
				</svg>
			</Box>
			<Node
				isLoading={isLoading}
				positions={'center'}
				text={centerCardValue}
				isEdit={isEdit}
				onInputChange={onInputChange}
				subjectText={subject.center}
			/>
			{/* Right arrow */}
			<Box display={'flex'} sx={styles.container}>
				<svg
					height="16"
					viewBox="0 0 270 16"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M269.707 8.70711C270.098 8.31658 270.098 7.68342 269.707 7.29289L263.343 0.928933C262.953 0.538409 262.32 0.538409 261.929 0.928933C261.538 1.31946 261.538 1.95262 261.929 2.34315L267.586 8L261.929 13.6569C261.538 14.0474 261.538 14.6805 261.929 15.0711C262.32 15.4616 262.953 15.4616 263.343 15.0711L269.707 8.70711ZM269 7L0 7.00001L0 9.00001L269 9L269 7Z"
						fill="url(#paint0_linear_8415_82129)"
					/>
					<defs>
						<linearGradient
							id="paint0_linear_8415_82129"
							x1="269.382"
							y1="8.00001"
							x2="0.608556"
							y2="7.23793"
							gradientUnits="userSpaceOnUse"
						>
							<stop stopColor="#C8003C" />
							<stop offset="1" stopColor="#FF437B" />
						</linearGradient>
					</defs>
				</svg>
			</Box>
			<Node
				isLoading={isLoading}
				positions={'right'}
				text={rightCardValue}
				isEdit={isEdit}
				onInputChange={onInputChange}
				subjectText={subject.right}
			/>
		</Box>
	)
}

const Section2 = (props: Props) => {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
	const { showAlert } = useToastContext()

	const [isEdit, setIsEdit] = useState(false)
	// State to store edited values subjectorarily
	const [editCardValue, setEditCardValue] = useState({
		left: props.leftCardValue,
		center: props.centerCardValue,
		right: props.rightCardValue,
	})
	const handleInputChange = (key: string, value: string) => {
		setEditCardValue((prev) => ({ ...prev, [key]: value }))
	}

	const onClickSaveEditPrompt = async () => {
		if (props.teeterTotterPrompt) {
			const finalPromptValues: TeeterTotterPrompt = {
				leftConstraint: editCardValue.left,
				rightConstraint: editCardValue.right,
				topic: editCardValue.center,
				promptId: props.teeterTotterPrompt.promptId,
			}

			props.changePrompts(finalPromptValues)
			try {
				if (showAlert) {
					showAlert({
						objectType: 'editPrompt',
						severity: ToastSeverity.SUCCESS,
					})
				}
			} catch (error) {
				if (showAlert) {
					showAlert({
						objectType: 'defaultError',
						severity: ToastSeverity.ERROR,
					})
				}
			}
		}
	}
	useEffect(() => {
		setEditCardValue({
			left: props.teeterTotterPrompt.leftConstraint,
			center: props.teeterTotterPrompt.topic,
			right: props.teeterTotterPrompt.rightConstraint,
		})
	}, [props])

	const buildResponseItem = (solution: string) => {
		if (!solution) return

		// Match ordered list items while preserving dollar amounts
		const matches = solution.match(/\d+\.\s.*?(?=\n\d+\.|\n?$)/gs)

		return (
			<Box bgcolor={'#FFDDE7'} padding={'1rem'}>
				<ol data-testid="gen-ai-response-list">
					{matches?.map((el: string, i: number) => (
						<Typography
							fontSize={16}
							color="black"
							component="li"
							sx={{ opacity: 0.87 }}
							key={`ai-solution-${i}`}
							variant="body1"
							style={{
								margin: '10px 0 10px 0',
							}}
						>
							{el.replace(/^\d+\.\s/, '').trim()}{' '}
							{/* Removes only the list number but keeps $1 */}
						</Typography>
					))}
				</ol>
			</Box>
		)
	}

	return (
		<>
			<Section2Title />
			<UserEducationModal />
			<Box sx={styles.mainContainer}>
				<Box marginRight={'3rem'} sx={styles.sideBar} />
				<Box
					display="flex"
					flexDirection="column"
					gap="2rem"
					flexGrow="1"
					paddingY={{ xs: '0', sm: '1rem' }}
				>
					<Box
						display={'flex'}
						flexDirection={{
							xs: 'column',
							sm: 'row',
						}}
						justifyContent={'space-between'}
						alignItems={{
							xs: 'flex-start',
							sm: 'center',
						}}
						gap={'1rem'}
					>
						<Typography sx={commonStyles.header}>
							Consider how both extremes of the following topic
							can inspire new ideas for your innovation topic.
						</Typography>
						<Box
							display={'flex'}
							flexDirection={'row'}
							justifyContent={'center'}
							alignItems={'center'}
							gap={'1rem'}
						></Box>
					</Box>
					<Box display="flex" justifyContent={'flex-end'}>
						<ButtonWithIconComponent
							sx={{ padding: 0 }}
							disabled={props.isLoading}
							iconSvg={refreshIcon}
							text="REFRESH"
							data-testid={`${TEST_ID}__refresh-button`}
							onClickAction={props.onRefreshPromptClick}
						/>
					</Box>
					<Box sx={styles.cardsContainer}>
						{isMobile
							? mobileCards(
									props.isLoading,
									isEdit,
									editCardValue.left,
									editCardValue.center,
									editCardValue.right,
									handleInputChange
								)
							: desktopAndTabletCards(
									props.isLoading,
									isEdit,
									editCardValue.left,
									editCardValue.center,
									editCardValue.right,
									handleInputChange
								)}
					</Box>
					{isEdit ? (
						<Box
							display="flex"
							sx={{
								flexDirection: {
									xs: 'column',
									sm: 'row',
								},
							}}
							gap="32px"
						>
							<Button
								color="secondary"
								variant="contained"
								data-testid={`${TEST_ID}__submit-idea-button`}
								onClick={async () => {
									setIsEdit(false)
									await onClickSaveEditPrompt()
								}}
							>
								<Typography>SAVE CHANGES</Typography>
							</Button>
							<Button
								disabled={props.isLoading}
								variant="text"
								color="secondary"
								data-testid={`${TEST_ID}__view-submit-ideas-button`}
								onClick={() => {
									setIsEdit(false)
									setEditCardValue({
										left: props.teeterTotterPrompt
											.leftConstraint,
										center: props.teeterTotterPrompt.topic,
										right: props.teeterTotterPrompt
											.rightConstraint,
									})
								}}
							>
								<Typography>Cancel</Typography>
							</Button>
						</Box>
					) : (
						<Box display={'flex'} justifyContent={'flex-end'}>
							<ButtonWithIconComponent
								disabled={props.isLoading}
								iconSvg={editIcon}
								text="EDIT"
								data-testid={`${TEST_ID}-edit-prompt`}
								onClickAction={() => setIsEdit(!isEdit)}
							/>
						</Box>
					)}
					<Typography sx={commonStyles.header}>
						{brainstormText.global.ideasDropdown}
					</Typography>
					<GeneratedExampleDropdown
						isLoading={props.isAiLoading}
						gameTypeEnum={GAME_TYPE.TEETER_TOTTER}
						headerText={''}
						customResponse={buildResponseItem(
							props.currentResponses
						)}
					/>
				</Box>
			</Box>
		</>
	)
}
export default Section2
